import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, Plus, Pencil, Trash2, Search, History as HistoryIcon, X } from "lucide-react";
import { toast } from "sonner";
import { MarkdownEditor, MarkdownPreview } from "@/components/MarkdownEditor";

const CATEGORIES = ["general", "governance", "finance", "vendors", "residents", "legal", "history", "decisions"];

const CAT_TONE = {
  governance: "border-violet-500/40 text-violet-300 bg-violet-500/10",
  finance: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  vendors: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  residents: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
  legal: "border-rose-500/40 text-rose-300 bg-rose-500/10",
  history: "border-white/10 text-zinc-400",
  decisions: "border-orange-500/40 text-orange-300 bg-orange-500/10",
  general: "border-white/10 text-zinc-400",
};

export default function BoardWikiPage() {
  const { propertyId, properties } = useProperty();
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const empty = useMemo(() => ({
    property_id: propertyId || "",
    title: "", body: "", category: "general", tags: [],
  }), [propertyId]);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (propertyId) params.property_id = propertyId;
      if (category && category !== "all") params.category = category;
      if (query) params.query = query;
      const { data } = await api.get("/board/wiki", { params });
      setPages(data.items || []);
      if (data.items?.length && !selected) setSelected(data.items[0]);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
  }, [propertyId, category, query, selected]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setForm((f) => ({ ...f, property_id: propertyId || f.property_id })); }, [propertyId]);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      property_id: p.property_id, title: p.title, body: p.body || "",
      category: p.category, tags: p.tags || [],
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/board/wiki/${editing.id}`, form);
        toast.success("Page updated");
        setSelected(data.page);
      } else {
        const { data } = await api.post("/board/wiki", form);
        toast.success("Page created");
        setSelected(data.page);
      }
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    setBusy(false);
  };

  const archive = async (p) => {
    if (!window.confirm(`Archive "${p.title}"?`)) return;
    try {
      await api.delete(`/board/wiki/${p.id}`);
      toast.success("Archived");
      if (selected?.id === p.id) setSelected(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  const openHistory = async (p) => {
    try {
      const { data } = await api.get(`/board/wiki/${p.id}/history`);
      setHistoryItems(data.items || []);
      setHistoryOpen(true);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load history"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Governance"
        title="Board Wiki"
        subtitle="A private, versioned knowledge base — decisions, house rules, and lessons learned. Every edit snapshots the prior version so next-term boards inherit the context."
      />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="wiki-add-btn">
          <Plus className="h-4 w-4 mr-1" /> New page
        </Button>
        <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2 pr-3">
          <Search className="h-3 w-3 text-zinc-500" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="Search title, body, or tag…"
                 className="h-8 border-0 bg-transparent focus-visible:ring-0 text-sm w-64"
                 data-testid="wiki-search" />
          {query && <X className="h-3 w-3 text-zinc-500 cursor-pointer" onClick={() => setQuery("")} />}
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-40 bg-white/[0.04] border-white/10" data-testid="wiki-category-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-zinc-500">{pages.length} page{pages.length === 1 ? "" : "s"}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
        {/* Pages list */}
        <div className="space-y-1 max-h-[70vh] overflow-y-auto scroll-slim">
          {pages.length === 0 ? (
            <Card className="p-6 text-center border-white/10" data-testid="wiki-empty">
              <BookOpen className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
              <div className="text-xs text-zinc-400">No pages yet. Capture a house rule, board decision, or lesson.</div>
            </Card>
          ) : pages.map((p) => (
            <button key={p.id} onClick={() => setSelected(p)}
                    data-testid={`wiki-page-${p.id}`}
                    className={"w-full text-left rounded-md border p-3 transition " +
                      (selected?.id === p.id
                        ? "border-orange-500/40 bg-orange-500/[0.05]"
                        : "border-white/5 bg-white/[0.01] hover:bg-white/[0.04]")}>
              <div className="text-sm text-zinc-100 font-medium truncate">{p.title}</div>
              <div className="flex items-center gap-1.5 mt-1">
                <Badge variant="outline" className={CAT_TONE[p.category] || CAT_TONE.general}>
                  {p.category}
                </Badge>
                <span className="text-[10px] text-zinc-500 ml-auto">v{p.version}</span>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 truncate">Updated {new Date(p.updated_at).toLocaleDateString()} · {p.updated_by_name}</div>
            </button>
          ))}
        </div>

        {/* Page detail */}
        {selected ? (
          <Card className="p-5 border-white/10 bg-card" data-testid={`wiki-detail-${selected.id}`}>
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="min-w-0">
                <div className="text-lg font-display font-semibold text-zinc-100">{selected.title}</div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <Badge variant="outline" className={CAT_TONE[selected.category] || CAT_TONE.general}>{selected.category}</Badge>
                  {(selected.tags || []).map((t) => (
                    <Badge key={t} variant="outline" className="border-white/10 text-zinc-400">#{t}</Badge>
                  ))}
                  <span className="text-[10px] text-zinc-500 ml-1">v{selected.version} · {new Date(selected.updated_at).toLocaleString()} · {selected.updated_by_name}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => openHistory(selected)} className="border-white/10 rounded-full"
                        data-testid={`wiki-history-${selected.id}`}>
                  <HistoryIcon className="h-3 w-3 mr-1" /> History
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(selected)} className="border-white/10 rounded-full"
                        data-testid={`wiki-edit-${selected.id}`}>
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
                <button onClick={() => archive(selected)} className="p-2 text-zinc-500 hover:text-red-300"
                        data-testid={`wiki-delete-${selected.id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
            <div className="text-sm text-zinc-300 leading-relaxed">
              {selected.body ? (
                <MarkdownPreview value={selected.body} />
              ) : (
                <span className="text-zinc-500 italic">No content yet — click Edit to start writing.</span>
              )}
            </div>
          </Card>
        ) : (
          <Card className="p-8 text-center border-white/10 text-zinc-500" data-testid="wiki-detail-empty">
            <BookOpen className="h-8 w-8 mx-auto mb-2" />
            <div className="text-sm">Select a page to read.</div>
          </Card>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[720px]" data-testid="wiki-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit page" : "New wiki page"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Every save snapshots the prior version so nothing gets lost.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Community</Label>
                <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                  <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="wiki-property">
                    <SelectValue placeholder="Choose a community" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                    {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="wiki-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="Board Q1 2026 · House-rule updates"
                     className="bg-white/[0.04] border-white/10" data-testid="wiki-title" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Body (Markdown with GFM support — tables, task lists, code blocks)</Label>
              <MarkdownEditor value={form.body}
                              onChange={(v) => setForm({ ...form, body: v })}
                              rows={14}
                              aiContext={{ title: form.title, tags: form.tags }}
                              dataTestId="wiki-body" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Tags (comma-separated)</Label>
              <Input value={(form.tags || []).join(", ")}
                     onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                     placeholder="q1, house-rules, decisions"
                     className="bg-white/[0.04] border-white/10" data-testid="wiki-tags" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={save} disabled={busy || !form.title || !form.property_id}
                    className="bg-orange-500 hover:bg-orange-600" data-testid="wiki-save-btn">
              {busy ? "Saving…" : (editing ? "Save changes" : "Create page")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[720px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Version history</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Every earlier revision, newest first.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {historyItems.length === 0 ? (
              <div className="text-xs text-zinc-500">No prior versions yet — this is the original.</div>
            ) : historyItems.map((h) => (
              <div key={h.id} className="rounded-md border border-white/10 bg-white/[0.02] p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="border-white/10 text-zinc-400">v{h.version}</Badge>
                  <span className="text-[11px] text-zinc-500">{new Date(h.snapshot_at).toLocaleString()} · {h.snapshot_of || "—"}</span>
                </div>
                <div className="text-sm text-zinc-100 font-medium">{h.title}</div>
                <div className="text-xs text-zinc-400 mt-1 max-h-40 overflow-y-auto">
                  <MarkdownPreview value={h.body} className="prose prose-invert prose-xs max-w-none" />
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
